import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Send, MoveUpRight } from 'lucide-react';

// Import images from assets/img/Aboutus8
import img1 from '../../assets/img/Aboutus8/1.png';
import img2 from '../../assets/img/Aboutus8/2.png';
import img3 from '../../assets/img/Aboutus8/3.png';
import img4 from '../../assets/img/Aboutus8/4.png';
import img5 from '../../assets/img/Aboutus8/5.png';
import img6 from '../../assets/img/Aboutus8/6.png';
import img7 from '../../assets/img/Aboutus8/7.png';
import img8 from '../../assets/img/Aboutus8/8.png';
import img9 from '../../assets/img/Aboutus8/9.png';

const Aboutus8 = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");

    const allImages = [img1, img2, img3, img4, img5, img6, img8, img9, img7];

    const visibleSlots = [
        { name: 't-left-top', offset: -3 },
        { name: 't-left-bottom', offset: -2 },
        { name: 't-mid-left', offset: -1 },
        { name: 't-center', offset: 0 },
        { name: 't-mid-right', offset: 1 },
        { name: 't-right-top', offset: 2 },
        { name: 't-right-bottom', offset: 3 },
    ];

    const trackSlots = [
        't-off-left-3', 't-off-left-2', 't-off-left-1',
        't-left-top', 't-left-bottom', 't-mid-left',
        't-center',
        't-mid-right', 't-right-top', 't-right-bottom',
        't-off-right-1', 't-off-right-2', 't-off-right-3'
    ];

    const testimonials = [
        {
            name: "Nick Lios, Lios Design Studio, LLC",
            quote: '"Thanks to Scan to BIM Service Company for their brilliant work. The process was professional and seamless, and the final result surpassed our expectations. We highly recommend them to anyone looking for a quality job at a reasonable price."',
            videoUrl: "https://www.youtube-nocookie.com/embed/Q0JHeqAlga4",
            fullTestimonialUrl: "/testimonial-1"
        },
        {
            name: "Watson, Watson Drafting Solution",
            quote: '"Partnering with Scan to BIM Service Company was an incredible experience. Their expertise in BIM modeling and commitment to our residential project truly aligned with our goals."',
            videoUrl: "https://www.youtube-nocookie.com/embed/Dw3tTpIp-sU",
            fullTestimonialUrl: "/testimonial-2"
        },
        {
            name: "Mayur Panchal, ScanBIM Solutions",
            quote: '"Their team demonstrated excellent management skills, and we were particularly impressed with their precision. Communication was smooth throughout."',
            videoUrl: "https://www.youtube-nocookie.com/embed/XqZsoesa55w",
            fullTestimonialUrl: "/testimonial-3"
        },
        {
            name: "Samaan Turk, Design Hive",
            quote: '"A visionary team that understands the intersection of technology and architecture. They delivered our BIM models with unmatched detail."',
            videoUrl: "https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ",
            fullTestimonialUrl: "/testimonial-4"
        },
        {
            name: "Sonam Vishwakarma, SpaceFlow",
            quote: '"The collaboration was seamless. They quickly aligned with our high standards and designed a platform that feels intuitive."',
            videoUrl: "https://www.youtube-nocookie.com/embed/3JZ_D3ELwOQ",
            fullTestimonialUrl: "/testimonial-5"
        },
        {
            name: "Aakanksha Damani, HR Link",
            quote: '"They truly listened, understood the vision, and provided flexibility and unbeatable planning. Higher transparency and quality deliverables."',
            videoUrl: "https://www.youtube-nocookie.com/embed/2Vv-BfVoq4g",
            fullTestimonialUrl: "/testimonial-6"
        },
        {
            name: "Rajesh Sharma, TechBuild",
            quote: '"Working with them felt like an extension of our own team. Theyre proactive, innovative, and always deliver on their promises."',
            videoUrl: "https://www.youtube-nocookie.com/embed/l482T0yNkeo",
            fullTestimonialUrl: "/testimonial-7"
        },
        {
            name: "Priya Patel, Arcadia",
            quote: '"The attention to detail and ability to capture the essence of our designs was remarkable. Stunning 3D realities."',
            videoUrl: "https://www.youtube-nocookie.com/embed/M7lc1UVf-VE",
            fullTestimonialUrl: "/testimonial-8"
        },
        {
            name: "Ready to be Our Next Success Story?",
            quote: "Join the businesses that trust mininventory to design, build, and scale impactful digital solution. Let’s turn vision into measurable results.",
            isCta: true,
            fullTestimonialUrl: "/testimonial-all",
            videoUrl: "/contact" // using videoUrl slot for the second CTA button link
        }
    ];

    const nextSlide = () => currentIndex < testimonials.length - 1 && setCurrentIndex(prev => prev + 1);
    const prevSlide = () => currentIndex > 0 && setCurrentIndex(prev => prev - 1);

    const openVideo = (url) => {
        setVideoUrl(url);
        setIsVideoModalOpen(true);
    };

    const current = testimonials[currentIndex];

    // AUTO PLAY LOGIC
    useEffect(() => {
        if (isVideoModalOpen) return; // Pause auto-play if video is playing

        const interval = setInterval(() => {
            if (currentIndex < testimonials.length - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                clearInterval(interval); // Stop at the end
            }
        }, 2000); // 2 seconds interval

        return () => clearInterval(interval);
    }, [currentIndex, isVideoModalOpen, testimonials.length]);

    // GET POSITIONS (CSS Classes for visible, Dynamic for off-screen)    // GET SLOT NAME FOR EACH IMAGE
    const getAvatarData = () => {
        return allImages.map((img, idx) => {
            const relIdx = idx - currentIndex;
            // Center index of trackSlots is 6
            const slotIdx = 6 + relIdx;
            const safeSlotIdx = Math.max(0, Math.min(trackSlots.length - 1, slotIdx));
            const posClass = trackSlots[safeSlotIdx];

            // Allow 4 slots on each side to be visible for a smooth exit/entry path
            const isVisible = relIdx >= -4 && relIdx <= 4;

            return {
                img,
                id: `img-${idx}`,
                posClass,
                opacity: isVisible ? 1 : 0,
                isMain: relIdx === 0
            };
        });
    };

    const avatars = getAvatarData();

    return (
        <section className="aboutus8-section">
            <div className="aboutus8-container">
                <div className="aboutus8-header">
                    <h2 className="title"><span className="text-secondary">What Our Clients</span> Have to Say About Us</h2>
                    <p className="desc centered">Behind every testimonial is a business problem solved, a system improved... Here's how our clients describe that journey</p>
                </div>

                <div className="card-outer-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '1100px', margin: '40px auto 0 auto' }}>
                    <div className={`aboutus8-card ${current.isCta ? 'cta-slide' : ''}`} style={{ margin: '0', overflow: 'hidden' }}>
                        <div className="avatar-cloud-wrapper" style={{ width: '100%', height: '180px', position: 'relative' }}>
                            <div className="avatar-cloud" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
                                {avatars.map((item) => (
                                    <motion.div
                                        key={item.id}
                                        layout
                                        layoutId={item.id}
                                        className={`avatar ${item.isMain ? 'large' : 'small'} ${item.posClass}`}
                                        transition={{ type: "spring", stiffness: 100, damping: 20 }}
                                        style={{ opacity: item.opacity }}
                                    >
                                        <img
                                            src={item.img.src || item.img}
                                            alt="Client"
                                            style={{
                                                objectFit: (item.img === img7 || (current.isCta && item.isMain)) ? 'contain' : 'cover'
                                            }}
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        <div className="testimonial-text-wrapper" style={{ minHeight: '120px', width: '100%', marginTop: '20px' }}>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentIndex}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.3 }}
                                    className="testimonial-content"
                                >
                                    <h3 className="client-name">{current.name}</h3>
                                    <p className="client-quote">{current.quote}</p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <div className="card-footer" style={{ position: 'relative', bottom: 'auto', marginTop: '20px' }}>
                            <button className={`btn-solid ${current.isCta ? 'white-btn' : ''}`}>
                                {current.isCta ? "View More Testimonials" : "Read Full Testimonial"}
                                {current.isCta && <MoveUpRight size={18} style={{ marginLeft: '8px' }} />}
                            </button>
                            {(current.videoUrl || !current.isCta) && (
                                <button className={`btn-outline ${current.isCta ? 'white-outline' : ''}`} onClick={() => !current.isCta && openVideo(current.videoUrl)}>
                                    {current.isCta ? "Partner With Us" : "Watch Video"}
                                    {current.isCta ? <Send size={18} style={{ marginLeft: '8px' }} /> : <Play size={16} fill="currentColor" />}
                                </button>
                            )}
                        </div>

                        <div className={current.isCta ? "ctaslide-btns" : ""}>
                            {currentIndex > 0 && (
                                <button className="nav-btn prev" onClick={prevSlide}>
                                    <ChevronLeft size={32} />
                                </button>
                            )}

                            {currentIndex < testimonials.length - 1 && (
                                <button className="nav-btn next" onClick={nextSlide}>
                                    <ChevronRight size={32} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isVideoModalOpen && (
                <div className="video-modal-overlay" onClick={() => setIsVideoModalOpen(false)}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="video-modal-close" onClick={() => setIsVideoModalOpen(false)}>&times;</button>
                        <iframe width="100%" height="450" src={videoUrl} title="Video" frameBorder="0" allowFullScreen></iframe>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Aboutus8;
